import { peso } from '../utils/formatCurrency';

// Spending is normal, not an error, so it stays neutral; balances are coloured by their sign.
export const kpiTones = {
  success: { text: 'text-success', bar: 'bg-success' },
  neutral: { text: 'text-navy', bar: 'bg-navy-light/60' },
  danger: { text: 'text-danger', bar: 'bg-danger' },
};

export const signTone = (value) =>
  value > 0 ? kpiTones.success : value < 0 ? kpiTones.danger : kpiTones.neutral;

// Change against the previous month. `neutral` skips the good/bad colouring (used for spending).
function ChangeIndicator({ current, previous, previousLabel, neutral }) {
  const diff = current - previous;
  const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const color = neutral || direction === 'flat' ? 'text-gray-600' : direction === 'up' ? 'text-success' : 'text-danger';
  const size =
    previous !== 0 ? `${((Math.abs(diff) / Math.abs(previous)) * 100).toFixed(1)}%` : peso(Math.abs(diff));

  return (
    <p className={`text-sm font-semibold whitespace-nowrap ${color}`}>
      {direction === 'flat' ? (
        'No change'
      ) : (
        <>
          <span aria-hidden="true">{direction === 'up' ? '▲' : '▼'}</span>
          <span className="sr-only">{direction === 'up' ? 'Up' : 'Down'}</span> {size}
        </>
      )}{' '}
      <span className="font-normal">vs {previousLabel}</span>
    </p>
  );
}

// Decorative: the figure it shows is always written out next to it.
function ProgressBar({ percent, barClass }) {
  return (
    <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
      <div
        className={`h-full rounded-full transition-all ${barClass}`}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
      />
    </div>
  );
}

/**
 * Headline figure card: label (with an optional change indicator), big value, caption and,
 * when `percent` is given, a progress bar. `value` is pre-formatted; null shows "--".
 * `change` is { current, previous, previousLabel, neutral } or null.
 */
function KpiCard({ label, value, tone = kpiTones.neutral, caption, percent, change, loading = false, children }) {
  return (
    <div className="flex flex-col gap-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-600">{label}</p>
        {!loading && change && <ChangeIndicator {...change} />}
      </div>
      <p className={`text-2xl lg:text-3xl font-bold break-words ${tone.text}`}>
        {loading || value == null ? '--' : value}
      </p>
      {caption !== undefined && <p className="text-sm text-gray-600">{loading ? '--' : caption}</p>}
      {percent !== undefined && <ProgressBar percent={loading ? 0 : percent ?? 0} barClass={tone.bar} />}
      {!loading && children}
    </div>
  );
}

export default KpiCard;
